// The headline numbers of a class or school report: where students are on their
// assignments, and the average of the released grades.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { T } from '../gamification/shared';
import type { ProgressCounts } from '../../services/classService';

/** A percentage of the rubric's scale, or a dash before anything is graded. */
export const formatAverage = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : `${Math.round(value)}%`;

interface Props {
  counts: ProgressCounts & { graded_attempts: number; average_score?: number | null };
  /** Tiles shown before the progress counts (class or student totals). */
  extra?: Array<{ label: string; value: number | string }>;
}

const tileStyle: React.CSSProperties = {
  flex: '1 1 120px',
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  padding: '0.6rem 0.8rem',
};

const ReportSummary: React.FC<Props> = ({ counts, extra = [] }) => {
  const { t } = useTranslation();
  const tiles = [
    ...extra,
    { label: t('studentAdmin.report.completed'), value: counts.completed },
    { label: t('studentAdmin.report.inProgress'), value: counts.in_progress },
    { label: t('studentAdmin.report.notStarted'), value: counts.not_started },
    { label: t('studentAdmin.report.gradedAttempts'), value: counts.graded_attempts },
    { label: t('studentAdmin.report.averageScore'), value: formatAverage(counts.average_score) },
  ];
  return (
    <dl style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '1rem 0' }}>
      {tiles.map(tile => (
        <div key={tile.label} style={tileStyle}>
          <dt style={{ fontSize: '0.75rem', color: T.muted, fontWeight: 600 }}>{tile.label}</dt>
          <dd style={{ margin: '2px 0 0', fontSize: '1.25rem', fontWeight: 700, color: T.text }}>{tile.value}</dd>
        </div>
      ))}
    </dl>
  );
};

export default ReportSummary;
